import { getC2cErrorCode, isForbiddenError } from '../../utils/c2cReceiving'

// Backend `error` code (C2cReceivingController, spec §3) → operator copy. Same contract
// as Orders/GenerateShipment/errorMessages.js and Returns/errorMessages.js: known codes get
// actionable copy, anything else degrades to the backend message, then to a generic line.
const T = 'c2c_receiving.errors'

export const C2C_ERROR_KEYS = {
  INVALID_PARAMS: `${T}.INVALID_PARAMS`,
  ROLL_CODE_NOT_FOUND: `${T}.ROLL_CODE_NOT_FOUND`,
  NOT_FOUND: `${T}.NOT_FOUND`,
  WRONG_WAREHOUSE: `${T}.WRONG_WAREHOUSE`,
  NOT_RECEIVABLE: `${T}.NOT_RECEIVABLE`,
  NOT_LABELABLE: `${T}.NOT_LABELABLE`,
  LABEL_GENERATION_FAILED: `${T}.LABEL_GENERATION_FAILED`,
  LABEL_GENERATION_IN_PROGRESS: `${T}.LABEL_GENERATION_IN_PROGRESS`,
  CUBBITO_RETURN_NOT_C2C: `${T}.CUBBITO_RETURN_NOT_C2C`,
}

// A response-less error is only a network outage when it actually came off the wire.
// axios 0.21 stamps `isAxiosError` and `request` on every transport failure (and
// `code: 'ECONNABORTED'` on a timeout; `ERR_NETWORK` is axios 1.x); a plain `Error` —
// e.g. the queue's own envelope-drift throw — is a bug, not a network problem, and
// must not send the operator to IT about the network.
const isTransportError = error => !!error && (error.isAxiosError === true || !!error.request || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK')

export const resolveC2cErrorMessage = (error, translate) => {
  // A timed-out axios request has no `response` but carries `code: 'ECONNABORTED'`;
  // it must not collapse into the generic "network" copy below (label generation can
  // legitimately take up to 2 minutes, so timeout gets its own actionable copy).
  if (error?.code === 'ECONNABORTED' && !error?.response) return translate(`${T}.timeout`)
  if (!error?.response) return translate(isTransportError(error) ? `${T}.network` : `${T}.generic`)
  if (isForbiddenError(error)) return translate(`${T}.forbidden`)

  const code = getC2cErrorCode(error)
  // Only a message the BACKEND sent is a usable fallback here; unlike getC2cErrorMessage,
  // this deliberately skips both `error.message` (axios's own "Request failed with status
  // code 500") and `data.error` — that field carries the CODE, and an unrecognised code
  // rendered raw is worse copy than the generic line below.
  const data = error.response.data
  const backendMessage = (typeof data?.message === 'string' && data.message) || ''

  if (code && C2C_ERROR_KEYS[code]) {
    const copy = translate(C2C_ERROR_KEYS[code])
    // The carrier's own (PII-scrubbed) reason is the actionable part for this one.
    if (code === 'LABEL_GENERATION_FAILED' && backendMessage) return `${copy}: ${backendMessage}`
    return copy
  }
  return backendMessage || translate(`${T}.generic`)
}
